import { FC, useCallback, useContext, useEffect, useState } from "react";
import { StopsService } from "../../../services/preventive-stops/stopsService";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useStopsStatus from "../../../hooks/api-calls/useStopsStatus";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import useFetch from "../../../hooks/useFetch";
import { toast } from "sonner";
import { DocumentsService } from "../../../services/document/documentsService";
import Page from "../../../layout/Page/Page";
import CustomTable from "../../../components/table/CustomTable";
import { ActionIcon, Button, Text, Tooltip } from "rizzui";
import { Link } from "react-router-dom";
import { menuRoutes } from "../../../router/menu";
import { HiOutlineEye } from "react-icons/hi2";
import { MdOutlineInsertPhoto, MdOutlineModeEditOutline } from "react-icons/md";
import DeletePopover from "../../../components/buttons/DeletePopover";
import { ListApiResponse } from "../../../type/entities/apiResponse-type";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import StopsFilters from "./StopsFilters";
import moment from "moment";
import StatusDropdown from "../../../components/forms/StatusDropdown";
import StopsFormModal from "../components/StopFormModal";
import Viewer from "react-viewer";
import { PrivilegeContext } from "../../../components/priviledge/PriviledgeProvider";
import cn from "../../../utils/classNames";
import {ReactComponent as Excel} from "../../../assets/svg/excel.svg";
import {ReactComponent as PDF} from "../../../assets/svg/pdf.svg";
import { IoCheckmarkDoneSharp } from "react-icons/io5";
import { FaSpinner } from "react-icons/fa";
import { carrionStopPresentSelector } from "../../../utils/SelectorMapping";
import { ImportStopsModal } from "./components/ImportStopsModal";


interface StopsListProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void
    isOpenImport: boolean;
    setIsOpenImport: (isOpen: boolean) => void;
}

const columnOrder = [
    'id',
    'stopDate',
    'park',
    'stopCode',
    'windDirection',
    'windSpeed',
    'cloudCoverage',
    'startTime',
    'stopTime',
    'resumeOrderTime',
    'resumeTime',
    'numMachines',
    'windTurbinesPreventiveStops',
    'downtimePerMachine',
    'numMachines',
    'totalDowntime',
    'cause',
    'comments',
    'user',
    'stopWithApp',
    'restartWithApp',
    'appIssue',
    'carrionPresence',
    'preventiveStopsStatus',
]

const StopsList: FC<StopsListProps> = ({ isOpen, setIsOpen, isOpenImport, setIsOpenImport }) => {

    // STATES

    const [ stop, setStop ] = useState<any>(null);
    const [ openFilters, setOpenFilters ] = useState<boolean>(false);
    const [ isLoadingImages, setIsLoadingImages ] = useState<boolean>(false);
    const [ viewerImages, setViewerImages] = useState<any>(null);
    const [ isValidating, setIsValidating ] = useState<boolean>(false);
    const [ isViewerOpen, setIsViewerOpen ] = useState(null);

    // HOOKS
    
    const { user } = useSelector((state: RootState) => state.auth);
    const { handleErrors } = useHandleErrors();
    const { userCan } = useContext(PrivilegeContext);
    const { getStopsStatusList } = useStopsStatus();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const stopsService = new StopsService();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE PARADAS
     * @EN GETS THE LIST OF STOPS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch ] = useFetch(useCallback(async () => {
        const response = await stopsService.listStops(filters);
        return response.getResponseData();
    }, [filters])) 
    // -------------------------------------------------------------------------------------------------------------
    

    // FUNCTIONS

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELIMINACION DE UNA PARADA
     * @EN HANDLES THE DELETION OF A STOP
     * 
     * @param id
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try{
            const response = await(await stopsService.deleteStop(id)).getResponseData();
            if(response.success) {
                toast.success('Parada eliminada correctamente');
                refetch();
            }else{
                handleErrors(response);
            }
        }catch(error) {
            handleErrors(error);
        }
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELIMINACION DE VARIAS PARADAS
     * @EN HANDLES THE DELETION OF MULTIPLE STOPS
     * 
     * @param ids
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleDeleteMultiple = async (ids: string[]) => {
        try{
            const response = await(await stopsService.deleteMultiStops(ids)).getResponseData();
            if(response.success) {
                toast.success('Paradas eliminadas correctamente');
                refetch();
            }else{
                handleErrors(response);
            }
        }catch(error) {
            handleErrors(error);
        }
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    // -------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIA EL ESTADO DE UNA PARADA
     * @EN CHANGES THE STATUS OF A STOP
     * 
     * @param id
     * @param status
     */
    // -------------------------------------------------------------------------------------------------------------
    const changeStatus = async (id: string, status: string) => {
        try{
            const response = await(await stopsService.changeStatusStop(id, status)).getResponseData();
            if(response.success) {
                toast.success('Estado de la parada cambiado correctamente');
            }else{
                handleErrors(response);
            }
        }catch(error) {
            handleErrors(error);
        }
    }
    // ----------------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTAR A EXCEL
     * @EN EXPORT TO EXCEL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const exportToExcel = async () => {
        const response = await (await stopsService.exportToExcel(filters)).getResponseData();
        if(response) {
            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'paradas_preventivas.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
        }else {
            handleErrors(response);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------

    const handleOpenViewer = async (stopRow : any) => {
        setIsLoadingImages(true);
        if (!stopRow.preventiveStopsPhotos || stopRow.preventiveStopsPhotos.length === 0) return;
        const images = await Promise.all(
            (stopRow.preventiveStopsPhotos || []).map(async (photo : any) => {
                const response = await new DocumentsService().renderDocument(photo.document.id);
                const file = new Blob([response?.getResponse()?.data], { type: 'image/' + photo.document.extension });
                return {
                    ...photo,
                    src: URL.createObjectURL(file),
                };
            })
        );
        setViewerImages(images);
        setIsViewerOpen(stopRow.id);
        setIsLoadingImages(false);
    };

    //----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERRAR MODAL
     * @EN CLOSE MODAL
     */ 
    //-----------------------------------------------------------------------------------------------------------------------
    const onClose =  () => {
        setIsOpen(false);
        setTimeout(() => {  
            setStop(null);
        }
        , 150);
    };
    //-----------------------------------------------------------------------------------------------------------------------

    const onCloseImport = () => {
        setIsOpenImport(false);
    };

    // RENDER
    return (
        <Page container='fluid'>
            <CustomTable 
                divClassName='max-h-table'
                classNameTable='fs-table'
                headerTableClassName='fs-table-header'
                id={'stops-list'}
                columnOrder={columnOrder}
                data={orderedData()}
                isLoading={loading}
                multiActions={[
                    {
                        label: 'Validar seleccionados',
                        render: (selectedRows: any, selectedRowsOriginal: any, reset: Function) => {
                            return (
                                <Button
                                    variant="outline"
                                    color="primary"
                                    className="flex items-center border-green text-green"   
                                    disabled={isValidating}
                                    onClick={async () => {
                                        try {
                                            setIsValidating(true);
                                            const response = (await stopsService.validateSelectedStops(selectedRows));
                                            if (response) {
                                                toast.success('Paradas validadas correctamente');
                                                refetch();
                                            } else {
                                                toast.error('Error al validar las paradas');
                                            }
                                        } catch (error: any) {
                                            toast.error(error.message);
                                        } finally {
                                            setIsValidating(false);
                                            reset();
                                        }
                                    }}
                                >
                                    <IoCheckmarkDoneSharp className="h-[20px] w-[20px] mr-2" />
                                    {isValidating ? <FaSpinner className="animate-spin h-[20px] w-[20px] mr-2" /> : <span>Validar</span>}
                                </Button>
                            )
                        }
                    }
                ]}
                addicionalButtons={
                    <>
                        {userCan('export', 'preventive_stops') && (
                            <>
                                <Tooltip
                                    size="sm"
                                    content={'Exportar a Excel'}
                                    placement="top"
                                    color="invert"
                                >
                                    <Button
                                        color="primary"
                                        onClick={exportToExcel}
                                        className={cn(['button-export-excel bg-white border-secondary text-black'])}
                                    >
                                        <Excel/>
                                        <span id="export-excel" className="ms-1">Exportar Excel</span>
                                    </Button>
                                </Tooltip>
                            </>
                        )}
                    </>
                }
                columnsNotShown={[]}
                columnsNoSortables={[]}
                isSortable
                overrideColumns={[
                    {
                        key: 'stopCode',
                        customHeader: 'Código',
                        render: (row: any) => {
                            if (userCan('get', 'preventive_stops')) {
                                return ( <a href={`${menuRoutes.stops.path}/${row.row.original.id}/profile/${menuRoutes.stops.profile.info}`} className='text-primary hover:text-primary hover:underline'>
                                    {row.row.original.stopCode || '-'}
                                </a> );
                            } else {
                                return <span>{row.row.original.stopCode || '-'}</span>;
                            }
                        }
                    },
                    {
                        key: 'stopDate',
                        customHeader: 'Fecha',
                        render: (row: any) => {
                            return row.row.original.stopDate ? `semana ${moment(row.row.original.stopDate.date).week()}` : '-';
                        }
                    },
                    {
                        key: 'windDirection',
                        customHeader: 'Dirección del Viento',
                        render: (row: any) => {
                            return row.row.original.windDirection || '0';
                        }
                    },
                    {
                        key: 'windSpeed',
                        customHeader: 'Velocidad del Viento',
                        render: (row: any) => {
                            return row.row.original.windSpeed || '0';
                        }
                    },
                    {
                        key: 'startTime',
                        customHeader: 'Hora de Llamada',
                        render: (row: any) => {
                            return row.row.original.startTime ? moment(row.row.original.startTime.date).format('HH:mm') : '-';
                        }
                    },
                    {
                        key: 'stopTime',
                        customHeader: 'Hora de Parada',
                        render: (row: any) => {
                            return row.row.original.stopTime ? moment(row.row.original.stopTime.date).format('HH:mm') : '-';
                        }
                    },
                    {
                        key: 'resumeOrderTime',
                        customHeader: 'Hora Fin',
                        render: (row: any) => {
                            return row.row.original.resumeOrderTime ? moment(row.row.original.resumeOrderTime.date).format('HH:mm') : '-';
                        }
                    },
                    {
                        key: 'resumeTime',
                        customHeader: 'Hora de reactivación del aerogenerador',
                        render: (row: any) => {
                            return row.row.original.resumeTime ? moment(row.row.original.resumeTime.date).format('HH:mm') : '-';
                        }
                    },
                    {
                        key: 'windTurbinesPreventiveStops',
                        customHeader: 'Aeros',
                        render: (row: any) => {
                            return row.row.original.windTurbinesPreventiveStops?.map((windTurbine: any) => windTurbine.windTurbine.name).join(', ') || '-';
                        }
                    },
                    {
                        key: 'totalDowntime',
                        customHeader: 'Horas Totales P. Máquinas',
                        render: (row: any) => {
                            return row.row.original.totalDowntime + ' min' || '0';
                        }
                    },

                    {
                        key: 'cloudCoverage',
                        customHeader: 'Cobertura de Nubes',
                        render: (row: any) => {
                            return row.row.original.cloudCoverage + '%' || '0';
                        }
                    },
                    {
                        key: 'cause',
                        customHeader: 'Causa',
                        render: (row: any) => {
                            return (
                                <Tooltip
                                    size="sm"
                                    content={row.row.original.cause || '-'}
                                    placement="top"
                                    color="invert"
                                >
                                    <div className="flex flex-col gap-2">
                                        <Text className="text-center text-capitalize">
                                            {row.row.original.cause || '-'}
                                        </Text>
                                    </div>
                                </Tooltip>
                            )
                        }
                    },
                    {
                        key: 'user',
                        customHeader: 'Técnico',
                        render: (row: any) => {
                            return row.row.original.user?.name + ' ' + row.row.original.user?.lastName || '-';
                        }
                    },
                    {
                        key: 'appIssue',
                        customHeader: 'Incidencias con la APP',
                        render: (row: any) => {
                            return row.row.original.appIssue || '-';
                        }
                    },
                    {
                        key: 'park',
                        customHeader: 'Parque Eólico',
                        render: (row: any) => {
                            return row.row.original.park?.name || '-';
                        }
                    },
                    {
                        key: 'speciesPreventiveStops',
                        customHeader: 'Especie de Parada',
                        render: (row: any) => {
                            return (
                                <div>
                                    {
                                        row.row.original.speciesPreventiveStops?.map((species: any) => (
                                            <div key={species.species.id}>
                                                <i className="italic">{species.species.scientificName}</i> ({species.numIndividuals})
                                            </div>
                                        ))
                                    }
                                </div>
                            )
                        }
                    },
                    {
                        key: 'preventiveStopsStatus',
                        customHeader: 'Estado',
                        render: (row: any) => {
                            const stopStatus = row.row.original?.preventiveStopsStatus;
                            const stopId = row.row.original?.id;
                            return (
                                <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id + stopStatus.id}>
                                    {(stopId && stopStatus) ? (
                                        <StatusDropdown
                                            divClassName="fs-table"
                                            optionsClassName="fs-table"
                                            selectedClassName="fs-table"
                                            entityId={row.row.original?.id}
                                            title="¿Estas seguro de cambiar el estado de la parada?"
                                            statesOptions={getStopsStatusList()}
                                            currentState={stopStatus}
                                            handleStateChange={(entityId: string, statusId: string) => changeStatus(stopId, statusId)}
                                            hasComments={false}
                                            disabled={!userCan('validate', 'preventive_stops')}
                                        />
                                    ) : "N/A"}
                                </div>
                            );
                        }
                    },
                    {
                        key: 'stopWithApp',
                        customHeader: 'Parado con la APP',
                        render: (row: any) => {
                            const translations: { [key: string]: string } = {
                                yes: 'Sí',
                                no: 'No',
                                partial: 'Parcial',
                            };
                            return translations[row.row.original.stopWithApp] || '';
                        }
                    },
                    
                    {
                        key: 'restartWithApp',
                        customHeader: 'Arrancado con la APP',
                        render: (row: any) => {
                            const translations: { [key: string]: string } = {
                                yes: 'Sí',
                                no: 'No',
                                partial: 'Parcial',
                            };
                            return translations[row.row.original.restartWithApp] || '';
                        }
                    },
                    {
                        key: 'downtimePerMachine',
                        render: (row: any) => {
                            return row.row.original.downtimePerMachine + ' min' || '-';
                        }  
                    },
                    {
                        key: 'preventiveStopsPhotos',
                        customHeader: 'Fotos',
                        render: (row: any) => {
                            return (
                                <p className="flex items-center gap-2">
                                <MdOutlineInsertPhoto
                                    className="h-4 w-4"
                                    size={20}
                                    onClick={() => handleOpenViewer(row.row.original)}
                                />
                                <span>{row.row.original.preventiveStopsPhotos?.length || 0}</span>
                                {row.row.original.preventiveStopsPhotos?.length !== 0 ? <Viewer
                                    visible={isViewerOpen === row.row.original.id}
                                    onClose={() => setIsViewerOpen(null)}
                                    images={viewerImages?.map((img: any) => ({ src: img.src, alt: img.document.name }))}
                                />
                                : null}
                            </p>
                            );
                        }
                    },
                    {
                        key: 'carrionPresence',
                        customHeader: 'Hay carroña',
                        render: (row: any) => {
                            return carrionStopPresentSelector.find((item: any) => item.value === row.row.original.carrionPresence)?.label || '-';
                        }
                    }
                ]}
                actions={[
                    {
                        label: 'View',
                        permissions: { group: 'preventive_stops', permission: 'get' },
                        render: (row: any) => {
                            return (
                                <Tooltip
                                    size="sm"
                                    content={'Ver paradas'}
                                    placement="top"
                                    color="invert"
                                >
                                    <Link
                                        to={`${menuRoutes.stops.path}/${row.id}/profile/${menuRoutes.stops.profile.info}`}>
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white h-5 w-5"
                                        >
                                            <HiOutlineEye className="h-3 w-3" size={20} />
                                        </ActionIcon>
                                    </Link>
                                </Tooltip>
                            );
                        },
                    },
                    {
                        label: 'Edit',
                        permissions: { group: 'preventive_stops', permission: 'edit' },
                        render: (row: any) => {
                            return (
                                <Tooltip
                                    size="sm"
                                    content={'Editar parada'}
                                    placement="top"
                                    color="invert"
                                >
                                    <ActionIcon
                                        as="span"
                                        size="sm"
                                        variant="outline"
                                        className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer h-5 w-5"
                                        onClick={() => {
                                            setIsOpen(true)
                                            setStop(row.id);
                                        }}
                                    >
                                        <MdOutlineModeEditOutline className="h-3 w-3" size={20} />
                                    </ActionIcon>
                                </Tooltip>
                            );
                        },
                    },  
                    {
                        label: 'Delete',
                        permissions: { group: 'preventive_stops', permission: 'delete' },
                        render: (row: any) => {
                            if (row.id === user?.id) return null;
                            return (
                                <Tooltip
                                    size="sm"
                                    content={'Eliminar parada'}
                                    placement="top"
                                    color="invert"
                                >
                                    <div>
                                        <DeletePopover
                                            actionIconClassName="h-5 w-5"
                                            title={`Eliminar parada`}
                                            description={`¿Estás seguro de que deseas eliminar la parada ${row.id}?`}
                                            onDelete={() => handleDelete(row.id)}
                                        />
                                    </div>
                                </Tooltip>
                            );
                        },
                    },
                ]}
                filters={filters}
                updateFilters={updateFilters}
                handleMultipleDelete={handleDeleteMultiple}
                updateFilterOrder={updateFilterOrder}
                defaultOrder={filters.filter_order || undefined}
                paginationData={{
                    pageSize: filters.limit,
                    currentPage: filters.page,
                    pageCount: (data as ListApiResponse) ? data.lastPage : 0,
                    totalCount: data?.totalRegisters,
                    handlePagination: updatePage,
                    handlePerPage: updatePageSize,
                }}
                toggleFilters={() => setOpenFilters(!openFilters)}
            />

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros de paradas'}>
                <StopsFilters
                    closeFiltersTab={(value: boolean) => setOpenFilters(value)}
                    updateFilters={updateFilters}
                    resetFilters={resetFilters}
                    filters={filters}
                />
            </FilterDrawerView>

            <ImportStopsModal
                isOpen={isOpenImport}
                onClose={onCloseImport}
                refetch={refetch}
            />

            
            <StopsFormModal
                isOpen ={isOpen}
                onClose={onClose}
                stop   ={stop}
                refetch={refetch}
            />



        </Page>
    )
}

export default StopsList;
